import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  ModeComment as ModeCommentIcon,
  Public as PublicIcon,
  RateReviewOutlined as EmptyIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';

import { Context } from '../../service/Context';
import { capitalizar, formatearFecha, ordenarLista } from '../../utils/UtilesComun';

/**
 * Modal para gestionar el listado completo de observaciones de un jugador.
 */
const ModalObservacionesJugador = ({
  open,
  onClose,
  idJugador,
  nombreJugador,
  observaciones = [],
  onUpdate,
  initialCreateMode = false,
}) => {
  const { idUsuario, anadirObservacion, editarObservacion, eliminarObservacion } = useContext(Context);

  const [formOpen, setFormOpen] = useState(false);
  const [editObsId, setEditObsId] = useState(null);
  const [formText, setFormText] = useState('');
  const [formPublica, setFormPublica] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Menú de visibilidad en línea para propietarios
  const [visibilidadAnchorEl, setVisibilidadAnchorEl] = useState(null);
  const [visibilidadTargetObs, setVisibilidadTargetObs] = useState(null);

  const handleOpenCreateForm = () => {
    setEditObsId(null);
    setFormText('');
    setFormPublica(false);
    setFormOpen(true);
  };

  useEffect(() => {
    if (open && initialCreateMode) {
      handleOpenCreateForm();
    }
  }, [open, initialCreateMode]);

  const handleOpenEditForm = (obs) => {
    setEditObsId(obs.idObservacion);
    setFormText(obs.observacion || '');
    setFormPublica(obs.publica || false);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditObsId(null);
    setFormText('');
    setFormPublica(false);
  };

  const handleCloseModal = () => {
    handleCloseForm();
    onClose();
  };

  const handleSaveForm = async () => {
    if (!formText.trim()) {
      return;
    }
    setGuardando(true);
    try {
      let updated;
      if (editObsId) {
        updated = await editarObservacion(editObsId, {
          idObservacion: editObsId,
          observacion: formText.trim(),
          publica: formPublica,
        });
      } else {
        updated = await anadirObservacion(idJugador, {
          idUsuario,
          observacion: formText.trim(),
          publica: formPublica,
        });
      }
      handleCloseForm();
      if (onUpdate && updated) {
        onUpdate(updated);
      }
    } catch (err) {
      console.error('Error al guardar observación:', err);
    } finally {
      setGuardando(false);
    }
  };

  const handleDelete = async (idObservacion) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta observación? Esta acción no se puede deshacer.')) {
      try {
        const updated = await eliminarObservacion(idObservacion);
        if (onUpdate && updated) {
          onUpdate(updated);
        }
      } catch (err) {
        console.error('Error al eliminar observación:', err);
      }
    }
  };

  const handleOpenVisibilityMenu = (event, obs) => {
    event.stopPropagation();
    setVisibilidadAnchorEl(event.currentTarget);
    setVisibilidadTargetObs(obs);
  };

  const handleCloseVisibilityMenu = () => {
    setVisibilidadAnchorEl(null);
    setVisibilidadTargetObs(null);
  };

  const handleChangeVisibility = async (nuevaPublica) => {
    if (!visibilidadTargetObs) {
      return;
    }
    const obs = visibilidadTargetObs;
    handleCloseVisibilityMenu();
    try {
      const updated = await editarObservacion(obs.idObservacion, {
        ...obs,
        publica: nuevaPublica,
      });
      if (onUpdate && updated) {
        onUpdate(updated);
      }
    } catch (err) {
      console.error('Error al cambiar visibilidad:', err);
    }
  };

  const observacionesOrdenadas = ordenarLista(observaciones, { field: 'timestamp', direction: 'desc' });
  const totalObs = observaciones.length;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        },
      }}
    >
      {/* Cabecera del Modal */}
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.25,
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '10px',
              backgroundColor: '#f0fdfa',
              border: '1px solid #99f6e4',
              color: '#0f766e',
            }}
          >
            <ModeCommentIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', lineHeight: 1.2 }}>
                Observaciones
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.15,
                  borderRadius: '12px',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  backgroundColor: totalObs > 0 ? '#e0f2fe' : '#f1f5f9',
                  color: totalObs > 0 ? '#0369a1' : '#64748b',
                  border: `1px solid ${totalObs > 0 ? '#bae6fd' : '#cbd5e1'}`,
                }}
              >
                {totalObs} {totalObs === 1 ? 'nota' : 'notas'}
              </Box>
            </Box>
            {nombreJugador && (
              <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
                {capitalizar(nombreJugador)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!formOpen && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenCreateForm}
              sx={{
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                backgroundColor: '#0f766e',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)',
                '&:hover': {
                  backgroundColor: '#0d9488',
                  boxShadow: '0 4px 6px rgba(15, 118, 110, 0.3)',
                },
              }}
            >
              Nueva observación
            </Button>
          )}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              color: '#94a3b8',
              '&:hover': {
                color: '#475569',
                backgroundColor: '#f1f5f9',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Contenido del Modal */}
      <DialogContent sx={{ p: 3, maxHeight: '72vh', overflowY: 'auto' }}>
        {/* Formulario integrado de creación / edición */}
        {formOpen && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              border: '1.5px solid #0f766e',
              boxShadow: '0 4px 12px rgba(15, 118, 110, 0.08)',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', mb: 1.5 }}>
              {editObsId ? 'Editar observación' : 'Añadir nueva observación'}
            </Typography>

            <TextField
              fullWidth
              multiline
              autoFocus
              minRows={3}
              maxRows={8}
              placeholder="Escribe aquí los detalles de la observación, fortalezas, evolución técnica o táctica del jugador..."
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0f766e' },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.88rem',
                  lineHeight: 1.5,
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 2,
              }}
            >
              {/* Selector de visibilidad */}
              <Tooltip
                title={
                  formPublica
                    ? 'Pública: visible para todos los usuarios autorizados del club'
                    : 'Privada: visible solo para ti'
                }
                arrow
              >
                <Box
                  onClick={() => setFormPublica(!formPublica)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: formPublica ? '#ecfdf5' : '#f1f5f9',
                    border: `1px solid ${formPublica ? '#a7f3d0' : '#cbd5e1'}`,
                    color: formPublica ? '#065f46' : '#475569',
                    fontSize: '0.80rem',
                    fontWeight: 600,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: formPublica ? '#d1fae5' : '#e2e8f0',
                    },
                  }}
                >
                  {formPublica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                  <span>{formPublica ? 'Pública (Club)' : 'Privada (Solo tú)'}</span>
                </Box>
              </Tooltip>

              {/* Botones de acción */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCloseForm}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#64748b',
                    borderColor: '#cbd5e1',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                      borderColor: '#94a3b8',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveForm}
                  disabled={guardando || !formText.trim()}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#0f766e',
                    '&:hover': { backgroundColor: '#0d9488' },
                  }}
                >
                  {guardando ? 'Guardando...' : editObsId ? 'Actualizar' : 'Guardar observación'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Listado de observaciones */}
        {observacionesOrdenadas && observacionesOrdenadas.length > 0 ? (
          <TableContainer
            sx={{
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: '18%', py: 1.25 }}>
                    Usuario
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: '14%', py: 1.25 }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: '48%', py: 1.25 }}>
                    Observación
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: '10%', py: 1.25 }}
                  >
                    Visibilidad
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: '10%', py: 1.25 }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {observacionesOrdenadas.map((obs) => {
                  const esPropietario = obs.idUsuario === idUsuario;
                  return (
                    <TableRow
                      key={obs.idObservacion}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': { backgroundColor: '#f8fafc' },
                      }}
                    >
                      {/* Usuario */}
                      <TableCell sx={{ py: 1.5, verticalAlign: 'top' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                          {obs.nombreUsuario || obs.idUsuario}
                        </Typography>
                      </TableCell>

                      {/* Fecha */}
                      <TableCell sx={{ py: 1.5, verticalAlign: 'top' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {formatearFecha(obs.timestamp)}
                        </Typography>
                      </TableCell>

                      {/* Texto Observación */}
                      <TableCell sx={{ py: 1.5, verticalAlign: 'top' }}>
                        <Typography
                          sx={{
                            fontSize: '0.84rem',
                            color: '#334155',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: 1.45,
                          }}
                        >
                          {obs.observacion}
                        </Typography>
                      </TableCell>

                      {/* Visibilidad */}
                      <TableCell align="center" sx={{ py: 1.5, verticalAlign: 'top' }}>
                        {esPropietario ? (
                          <Tooltip title="Clic para cambiar visibilidad" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenVisibilityMenu(e, obs)}
                              sx={{
                                color: obs.publica ? '#059669' : '#64748b',
                                backgroundColor: obs.publica ? '#ecfdf5' : '#f1f5f9',
                                border: `1px solid ${obs.publica ? '#a7f3d0' : '#e2e8f0'}`,
                                p: 0.6,
                                '&:hover': {
                                  backgroundColor: obs.publica ? '#d1fae5' : '#e2e8f0',
                                },
                              }}
                            >
                              {obs.publica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={obs.publica ? 'Pública' : 'Privada'} arrow>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                p: 0.6,
                                borderRadius: '50%',
                                color: obs.publica ? '#059669' : '#64748b',
                              }}
                            >
                              {obs.publica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Acciones */}
                      <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'top' }}>
                        {esPropietario ? (
                          <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                            <Tooltip title="Editar observación" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditForm(obs)}
                                sx={{
                                  color: '#0f766e',
                                  '&:hover': { backgroundColor: '#f0fdfa' },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar observación" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(obs.idObservacion)}
                                sx={{
                                  color: '#ef4444',
                                  '&:hover': { backgroundColor: '#fef2f2' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                            Lectura
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          !formOpen && (
            <Box
              sx={{
                py: 6,
                px: 3,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                border: '1.5px dashed #cbd5e1',
                backgroundColor: '#f8fafc',
              }}
            >
              <EmptyIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#334155', mb: 0.5 }}>
                Sin observaciones registradas
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: '#64748b', maxWidth: 420, mb: 2.5 }}>
                Este jugador aún no tiene anotaciones ni observaciones registradas. Puedes añadir notas sobre su
                rendimiento, informes de seguimiento o evolución.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateForm}
                sx={{
                  borderRadius: '20px',
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: '#0f766e',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  '&:hover': { backgroundColor: '#0d9488' },
                }}
              >
                Añadir primera observación
              </Button>
            </Box>
          )
        )}

        {/* Menú de selección de visibilidad rápida */}
        <Menu
          anchorEl={visibilidadAnchorEl}
          open={Boolean(visibilidadAnchorEl)}
          onClose={handleCloseVisibilityMenu}
          PaperProps={{
            sx: {
              borderRadius: '10px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e2e8f0',
            },
          }}
        >
          <MenuItem
            onClick={() => handleChangeVisibility(false)}
            sx={{ fontSize: '0.82rem', gap: 1, py: 1 }}
            selected={visibilidadTargetObs && !visibilidadTargetObs.publica}
          >
            <LockIcon sx={{ fontSize: 16, color: '#64748b' }} />
            <span>Privada (Solo tú)</span>
          </MenuItem>
          <MenuItem
            onClick={() => handleChangeVisibility(true)}
            sx={{ fontSize: '0.82rem', gap: 1, py: 1 }}
            selected={visibilidadTargetObs && visibilidadTargetObs.publica}
          >
            <PublicIcon sx={{ fontSize: 16, color: '#059669' }} />
            <span>Pública (Club)</span>
          </MenuItem>
        </Menu>
      </DialogContent>
    </Dialog>
  );
};

ModalObservacionesJugador.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idJugador: PropTypes.number.isRequired,
  nombreJugador: PropTypes.string,
  observaciones: PropTypes.array,
  onUpdate: PropTypes.func,
  initialCreateMode: PropTypes.bool,
};

export default ModalObservacionesJugador;
